/**
 * TASK-TGE-001: Border Hex Detection Algorithm
 *
 * Identifies hexes at the edge of deployment zones and determines which edges face outward.
 * Used for rendering glowing border lines in Tron-style grid effects.
 */

import type { AxialCoordinate } from '@drawn-of-war/shared';

/**
 * Edge definition for a hex border
 */
export interface HexEdge {
  /** Starting point of the edge */
  start: { x: number; y: number };
  /** Ending point of the edge */
  end: { x: number; y: number };
  /** Edge index (0-5) on the hex */
  edgeIndex: number;
}

/**
 * Find all hexes that are at the border of a zone
 * A hex is a border hex if at least one of its neighbors is NOT in the zone
 *
 * @param zoneHexes - Set of hex coordinate keys (format: "q{q}r{r}")
 * @returns Set of hex coordinate keys that are at the border
 */
export function findBorderHexes(zoneHexes: Set<string>): Set<string> {
  const borderHexes = new Set<string>();

  for (const hexKey of zoneHexes) {
    const hex = parseHexKey(hexKey);
    const neighbors = getNeighbors(hex);

    // Check if any neighbor is outside the zone
    const hasExternalNeighbor = neighbors.some(
      (neighbor) => !zoneHexes.has(hexToKey(neighbor))
    );

    if (hasExternalNeighbor) {
      borderHexes.add(hexKey);
    }
  }

  return borderHexes;
}

/**
 * Get the edges of a hex that face outward from the zone
 * An edge faces outward if the neighbor in that direction is NOT in the zone
 *
 * @param hexKey - Hex coordinate key
 * @param zoneHexes - Set of all hexes in the zone
 * @param hexToPixel - Function to convert hex coordinates to pixel coordinates
 * @param getHexCorners - Function to get the corner points of a hex
 * @returns Array of edge definitions for outward-facing edges
 */
export function getBorderEdges(
  hexKey: string,
  zoneHexes: Set<string>,
  hexToPixel: (hex: AxialCoordinate) => { x: number; y: number },
  getHexCorners: (center: { x: number; y: number }) => { x: number; y: number }[]
): HexEdge[] {
  const hex = parseHexKey(hexKey);
  const neighbors = getNeighbors(hex);
  const center = hexToPixel(hex);
  const corners = getHexCorners(center);

  const borderEdges: HexEdge[] = [];

  // Check each of the 6 neighbors
  neighbors.forEach((neighbor, index) => {
    const neighborKey = hexToKey(neighbor);

    // If neighbor is NOT in the zone, this edge is a border
    if (!zoneHexes.has(neighborKey)) {
      // Edge i connects corner i to corner (i+1) % 6
      const edgeStart = corners[index];
      const edgeEnd = corners[(index + 1) % 6];

      borderEdges.push({
        start: edgeStart,
        end: edgeEnd,
        edgeIndex: index,
      });
    }
  });

  return borderEdges;
}

/**
 * Convert hex coordinates to a string key
 */
export function hexToKey(hex: AxialCoordinate): string {
  return `q${hex.q}r${hex.r}`;
}

/**
 * Parse a hex key back to coordinates
 */
export function parseHexKey(key: string): AxialCoordinate {
  const match = key.match(/q(-?\d+)r(-?\d+)/);
  if (!match) {
    throw new Error(`Invalid hex key format: ${key}`);
  }
  return {
    q: parseInt(match[1], 10),
    r: parseInt(match[2], 10),
  };
}

/**
 * Get the 6 neighbors of a hex in axial coordinates
 * Order: NE, E, SE, SW, W, NW (clockwise from top-right)
 */
export function getNeighbors(hex: AxialCoordinate): AxialCoordinate[] {
  const { q, r } = hex;

  return [
    { q: q + 1, r: r - 1 }, // NE
    { q: q + 1, r: r },     // E
    { q: q, r: r + 1 },     // SE
    { q: q - 1, r: r + 1 }, // SW
    { q: q - 1, r: r },     // W
    { q: q, r: r - 1 },     // NW
  ];
}
